/* eslint-disable no-console */
import { Box, Link, Stack, SvgIcon, Typography, styled } from '@mui/material';

import { AppConfig, MapMarkerResult, NameUtils, Policy } from '@oh-vcp/components-common';
import {
  LocationDotLightActive,
  MapService,
  ResultHeaderIcon,
  UserLargeBlueSVGIcon,
  HandHoldingHeartIcon,
  HospitalIcon,
  BuildingUserSmSVGIcon,
  ActionItemParams,
  ArrowLeft
} from '@oh-vcp/components-ui';
import { InfoWindowF, Marker } from '@react-google-maps/api';

import React, { FC, useEffect, useRef, useState } from 'react';
import SearchListToolbar from '../searchListToolbar/SearchListToolbar';
import { useSearchResultsViewContext } from '../searchResults/SearchResultsViewContext';

// gm-ui-hover-effect gm-style-iw gm-style-iw-c
// override the default styles for the info window

const StyledInfoWindow = styled(InfoWindowF)(() => ({
  '&.gm-style-iw': {
    '&.gm-style-iw-c': {
      background: 'none'
    }
  }
}));

interface MapMarkerWithInfoWindowProps {
  marker: MapMarkerResult;
  isActive: boolean;
  icon: string;
  activeIcon: string;
  policy?: Policy | undefined;
  actionParams?: ActionItemParams;
  hideInfoWindow?: boolean | null;
  onClick: () => void;
  onNext?: (locationSequence: Number) => void;
  locationCount?: number;
}

const MapMarkerWithInfoWindow: FC<MapMarkerWithInfoWindowProps> = ({
  marker,
  isActive,
  icon,
  activeIcon,
  policy,
  actionParams,
  hideInfoWindow,
  onClick,
  onNext,
  locationCount
}) => {
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [streetViewImage, setStreetViewImage] = useState<string | null>(null);
  const { lat, lng, displayName, address, profileUrl, locationSequence = 0 } = marker; // Provide default value for locationSequence
  const { city, address: street, postalCode, province } = address;

  const handleOnMarkerLoad = (newMarker: google.maps.Marker) => {
    markerRef.current = newMarker;
  };

  const InfoWindowIcon = styled(Box)(() => ({
    flexBasis: 1
  }));

  const MarkerToolbarContainer = styled(Box)(() => ({
    position: 'absolute',
    right: -11,
    bottom: 55
  }));
  const { selectedResult } = useSearchResultsViewContext();

  const activeLocation = isActive &&
    markerRef.current != null &&
    selectedResult?.locationSequence != null &&
    marker.locationSequence === selectedResult?.locationSequence;

  useEffect(() => {
    const isActiveLoc = isActive &&
    markerRef.current != null &&
    selectedResult?.locationSequence != null &&
    marker.locationSequence === selectedResult?.locationSequence;

    if (isActiveLoc) {
      const panorama = new google.maps.StreetViewPanorama(document.createElement('div'), {
        position: { lat, lng },
        pov: { heading: 165, pitch: 0 },
        zoom: 1
      });
      const streetViewService = new google.maps.StreetViewService();
      streetViewService.getPanorama(
        {
          location: { lat, lng },
          radius: 50
        },
        (data, status) => {
          if (status === 'OK' && data && data.location) {
            panorama.setPano(data.location.pano);
            const streetViewUrl = panorama.getPano();
            setStreetViewImage(
              `https://maps.googleapis.com/maps/api/streetview?size=200x100&pano=${streetViewUrl}&key=${AppConfig.googleAPIKey}`
            );
          }
        }
      );
    } else {
      setStreetViewImage(null);
    }
  }, [isActive, lat, lng, locationSequence, selectedResult]);

  const getMarkerIcon = (): React.ElementType => {
    switch (marker.resultType) {
      case 'hs_services':
        return BuildingUserSmSVGIcon;
      case 'site_with_systems':
        return HospitalIcon;
      case 'community_services':
        return HandHoldingHeartIcon;
      case 'people':
      default:
        return UserLargeBlueSVGIcon;
    }
  };
  return (
    <>
      <Marker
        position={{
          lat,
          lng
        }}
        icon={activeLocation ? activeIcon : icon}
        onClick={() => onClick()}
        onLoad={handleOnMarkerLoad}
        options={{
          zIndex: activeLocation ? 2 : 0
        }}
      />
      {activeLocation && markerRef.current && !hideInfoWindow && (
        <StyledInfoWindow
          options={{ maxWidth: 450, minWidth: 350 }}
          anchor={markerRef.current}
          onLoad={(infoWindow) => {
            infoWindowRef.current = infoWindow;
          }}>
          <Stack
            direction="column"
            sx={{
              opacity: 1
            }}>
            <>
              {(locationCount ?? 0) > 1 && (
                <SvgIcon
                  component={ArrowLeft}
                  inheritViewBox
                  onClick={() => {
                    if (locationSequence !== null) {
                      onNext?.(locationSequence - 1); // Call onNext with a valid number
                    }
                  }}
                  sx={{
                    width: '44px',
                    height: '44px',
                    position: 'absolute',
                    left: '-18%',
                    top: '55%',
                    boxShadow: '0px 3px 6px #00000029',
                    backgroundColor: 'white',
                    borderRadius: '22px',
                    padding:'5px',
                    '&:hover': { cursor: 'pointer' }
                  }}
                />
              )}

              {activeLocation && streetViewImage && (
                <img src={streetViewImage} alt="Street View" style={{ padding: 0 }} />
              )}

              <Stack
                direction="column"
                sx={{
                  width: 'inherit',
                  position: 'relative',
                  opacity: 1
                }}>
                <Stack direction="row" sx={{ p: 1 }}>
                  {displayName && (
                    <>
                      <InfoWindowIcon>
                        <ResultHeaderIcon>
                          <SvgIcon
                            component={getMarkerIcon()}
                            inheritViewBox
                            sx={{ width: '15px', height: '15px' }}
                          />
                        </ResultHeaderIcon>
                      </InfoWindowIcon>
                      <Link href={profileUrl} pl={0} justifyContent="center">
                        <Typography variant="body1" flexGrow={1}>
                          {displayName}
                        </Typography>
                      </Link>
                    </>
                  )}
                  {policy && actionParams && (
                    <MarkerToolbarContainer>
                      <SearchListToolbar policy={policy} actionParams={actionParams} />
                    </MarkerToolbarContainer>
                  )}
                </Stack>
                <Stack direction="row" sx={{pl: 1, pb: 1 }} columnGap={2}>
                  <InfoWindowIcon>
                    <LocationDotLightActive />
                  </InfoWindowIcon>
                  <Link href={MapService.getMapUrl(address)} target="_blank">
                    {NameUtils.makeAddressName(
                      street ?? '',
                      city ?? '',
                      province ?? '',
                      postalCode ?? ''
                    )}
                  </Link>
                </Stack>
              </Stack>
              {(locationCount ?? 0) > 1 && (
                <SvgIcon
                  component={ArrowLeft}
                  inheritViewBox
                  onClick={() => {
                    if (locationSequence !== null) {
                      onNext?.(locationSequence + 1);
                    }
                  }}
                  sx={{
                    width: '44px',
                    height: '44px',
                    position: 'absolute',
                    zIndex: 'modal',
                    left: '106%',
                    top: '55%',
                    boxShadow: '0px 3px 6px #00000029',
                    backgroundColor: 'white',
                    borderRadius: '22px',
                    padding:'5px',
                    transform: 'rotate(180deg)',
                    '&:hover': { cursor: 'pointer' }
                  }}
                />
              )}
            </>
          </Stack>
        </StyledInfoWindow>
      )}
    </>
  );
};

export default MapMarkerWithInfoWindow;
